import { createContext, useState, useEffect, useContext } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn, signOut, getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword } from 'aws-amplify/auth';

// Configure Amplify
Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: 'us-east-1_syKDNP1p6',
            userPoolClientId: '22uu46vteejj2n3fbd6kuioe11',
            loginWith: {
                email: true
            }
        }
    }
});

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check if user is already authenticated on mount
    useEffect(() => {
        checkUser();
    }, []);

    const checkUser = async () => {
        try {
            const currentUser = await getCurrentUser();
            setUser(currentUser);
        } catch (err) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = async (username, password) => {
        try {
            setError(null);
            setLoading(true);

            // Check if there's already a user signed in
            try {
                const existingUser = await getCurrentUser();
                if (existingUser) {
                    // User already signed in, just update state
                    setUser(existingUser);
                    setLoading(false);
                    return { success: true, alreadySignedIn: true };
                }
            } catch (err) {
                // No user signed in, continue with login
            }

            const { isSignedIn, nextStep } = await signIn({ username, password });

            if (isSignedIn) {
                const currentUser = await getCurrentUser();
                setUser(currentUser);
                setLoading(false);
                return { success: true };
            }

            setLoading(false);
            return { success: false, nextStep };
        } catch (err) {
            // If error is "There is already a signed in user"
            if (err.name === 'UserAlreadyAuthenticatedException' ||
                (err.message && err.message.includes('already a signed in user'))) {
                try {
                    console.log('User already authenticated, signing out and retrying...');
                    // Sign out the existing user first
                    await signOut();
                    // Small delay to ensure signout completes
                    await new Promise(resolve => setTimeout(resolve, 500));
                    // Then try to sign in again
                    const { isSignedIn } = await signIn({ username, password });
                    if (isSignedIn) {
                        const currentUser = await getCurrentUser();
                        setUser(currentUser);
                        setLoading(false);
                        return { success: true };
                    }
                } catch (retryErr) {
                    setError(retryErr.message);
                    console.error('Login retry error:', retryErr);
                    setLoading(false);
                    return { success: false, error: retryErr.message };
                }
            }

            setError(err.message);
            console.error('Login error:', err);
            setLoading(false);
            return { success: false, error: err.message };
        }
    };

    const logout = async () => {
        try {
            await signOut();
            setUser(null);
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    const getAccessToken = async () => {
        try {
            const session = await fetchAuthSession();
            return session.tokens?.idToken?.toString();
        } catch (err) {
            console.error('Error getting access token:', err);
            return null;
        }
    };

    const requestPasswordReset = async (username) => {
        try {
            const output = await resetPassword({ username });
            return {
                success: true,
                destination: output.nextStep.codeDeliveryDetails?.destination
            };
        } catch (err) {
            console.error('Password reset request error:', err);
            return { success: false, error: err.message };
        }
    };

    const confirmPasswordReset = async (username, confirmationCode, newPassword) => {
        try {
            await confirmResetPassword({ username, confirmationCode, newPassword });
            return { success: true };
        } catch (err) {
            console.error('Password reset confirmation error:', err);
            return { success: false, error: err.message };
        }
    };

    const value = {
        user,
        loading,
        error,
        login,
        logout,
        getAccessToken,
        requestPasswordReset,
        confirmPasswordReset,
        isAuthenticated: !!user
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
